import type { EChartsOption } from 'echarts';

import { buildAnimationConfig } from '../shared/animation';
import { buildTooltipConfig } from '../shared/tooltip';
import { buildSingleBarBreakdownSeries } from './series';
import type { SingleBarBreakdownChartConfig, SingleBarBreakdownDatum } from './types';

export type { SingleBarBreakdownChartConfig, SingleBarBreakdownDatum } from './types';

const getSeriesIndex = (params: unknown): number | undefined => {
  if (!params || typeof params !== 'object' || !('seriesIndex' in params)) return undefined;
  const { seriesIndex } = params;
  return typeof seriesIndex === 'number' ? seriesIndex : undefined;
};

export const buildSingleBarBreakdownChartOption = (
  data: SingleBarBreakdownDatum[],
  config: SingleBarBreakdownChartConfig,
): EChartsOption => {
  const total = data.reduce((sum, datum) => sum + datum.value, 0);

  return {
    ...buildAnimationConfig(config.animated),
    grid: { top: 0, right: 0, bottom: 0, left: 0 },
    tooltip: {
      ...buildTooltipConfig(config.theme),
      // The formatter always returns fully-styled HTML that renders its own background,
      // border, and padding. Clearing the ECharts shell prevents the double-border.
      backgroundColor: 'transparent',
      borderWidth: 0,
      padding: 0,
      trigger: 'item',
      formatter: (params: unknown): string => {
        const seriesIndex = getSeriesIndex(params);
        return seriesIndex === undefined ? '' : (data[seriesIndex]?.tooltipHtml ?? '');
      },
    },
    xAxis: {
      type: 'value',
      min: 0,
      max: total,
      show: false,
    },
    yAxis: {
      type: 'category',
      data: ['bar'],
      show: false,
    },
    series: buildSingleBarBreakdownSeries(data, config.backgroundColor),
  };
};
